import { ILoadingService, TLoadingRequest } from '@aksia/infrastructure';
import {
  computed,
  Injectable,
  linkedSignal,
  signal,
  WritableSignal,
} from '@angular/core';

@Injectable({
  providedIn: 'root',
})
export class LoadingService implements ILoadingService {
  //#region Properties

  registeredKeys = new Set<string>();
  loadRequests: WritableSignal<Map<string, Array<TLoadingRequest>>> = signal(
    new Map(),
  );
  isLoading = linkedSignal(() => this.loadRequests().size > 0);
  pageLoading = computed(() => {
    let latestMessage = this.loadRequests()
      ?.values()
      ?.flatMap((request) => request)
      ?.filter((request) => request.type === 'page')
      ?.toArray()
      ?.at(-1)?.message;
    return latestMessage;
  });

  loadingTimeouts: Map<string, number> = new Map();

  /* loadingPercentage: Signal<string | undefined> = computed(() => {
    let loadedCount = [...this.loadRequests()?.values()]?.filter(
      (request: TLoadingRequest) => request.status === 'loaded',
    ).length;
    let requestCount = [...this.loadRequests()?.values()]?.length;
    return `${((loadedCount / requestCount) * 100).toFixed(0)} %`;
  }); */

  //#region Functions

  public registerLoadingKey(key: string) {
    if (!key) {
      return console.error(
        `Loading key ${key} is not valid. Cannot register it.`,
      );
    }
    if (!this.registeredKeys.has(key)) {
      this.registeredKeys.add(key);
    }
  }

  public addLoadRequest(
    key: string,
    options: TLoadingRequest = { type: 'area' },
  ) {
    if (!this.registeredKeys.has(key)) {
      return console.error(
        `Loading key ${key} is not registered. Registered keys are: ${[...this.registeredKeys]}`,
      );
    }

    let loadRequests = this.loadRequests();
    let loadRequest = loadRequests.get(key);

    options.type = options.type ?? 'area';
    options.title = options.title ?? key;
    options.action = options.action ?? 'Loading';
    options.message = `${options.action} ${options.title}...`;

    if (loadRequest) {
      loadRequest.push(options);
    } else {
      loadRequests.set(key, [options]);
    }

    this.loadRequests.set(new Map([...loadRequests]));

    if (this.loadingTimeouts.has(key)) {
      clearTimeout(this.loadingTimeouts.get(key)!);
      this.loadingTimeouts.delete(key);
    }

    if (options.timeout) {
      const timeoutId = setTimeout(() => {
        this.setLoadResponse(key, options.title);
      }, options.timeout);
      this.loadingTimeouts.set(key, timeoutId as unknown as number);
    }
  }

  public setLoadResponse(key: string, title?: string) {
    this.loadRequests.update((allRequestTypes) => {
      if (!title) {
        allRequestTypes.delete(key);
      } else {
        let loadRequestType = allRequestTypes.get(key);
        if (loadRequestType?.length! > 0) {
          loadRequestType = loadRequestType?.filter(
            (request) => request.title !== title,
          );

          if (loadRequestType?.length! === 0) {
            allRequestTypes.delete(key);
          } else {
            allRequestTypes.set(key, loadRequestType!);
          }
        }
      }
      return new Map([...allRequestTypes]);
    });
  }

  public areaLoading(key: string) {
    this.registerLoadingKey(key);
    return computed(() => this.loadRequests()?.get(key)?.at(0)?.message);
  }

  public removeLoadRequest(key: string) {
    let loadRequests = this.loadRequests();
    loadRequests.delete(key);
    this.loadRequests.set(new Map([...loadRequests]));
  }

  public resetLoadRequests() {
    this.loadRequests.set(new Map());
  }

  //#endregion
}
